import React, { useState, useEffect, useCallback } from 'react';
import { ontologyApi, sourcesApi } from '../api';
import { apiService } from '../../../services/api';
import { getCurrentUser } from '../../../utils/roleCheck';
import { isUuidString } from '../workspaceUtils';
import JsonViewer from '../shared/JsonViewer';

export default function OntologyTab({ workspaceId }) {
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [synthLoading, setSynthLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [diffOld, setDiffOld] = useState('');
  const [diffNew, setDiffNew] = useState('');
  const [diffResult, setDiffResult] = useState(null);

  const [publishModal, setPublishModal] = useState(null);
  const [publishWorkspace, setPublishWorkspace] = useState('');
  const [publishGraphName, setPublishGraphName] = useState('');
  const [publishWorkspaces, setPublishWorkspaces] = useState([]);
  const [publishWorkspacesLoading, setPublishWorkspacesLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState(null);

  const [sourceDocModal, setSourceDocModal] = useState(null);
  const [sourceDocMarkdown, setSourceDocMarkdown] = useState('');
  const [sourceDocFilename, setSourceDocFilename] = useState('');
  const [sourceDocLoading, setSourceDocLoading] = useState(false);
  const [sourceDocError, setSourceDocError] = useState(null);
  const [sourceDocCopied, setSourceDocCopied] = useState(false);

  const [binding, setBinding] = useState(null);
  const [bindingLoading, setBindingLoading] = useState(false);
  const [bindingError, setBindingError] = useState(null);
  const [bindingGraphId, setBindingGraphId] = useState('');
  const [bindingGraphName, setBindingGraphName] = useState('');

  // Lossless materialization -- round-trip validation state.
  const [latestRun, setLatestRun] = useState(null);
  const [reportModal, setReportModal] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [materializeLoading, setMaterializeLoading] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      setLoadError(null);
      const [v, s] = await Promise.all([
        ontologyApi.listVersions(workspaceId),
        sourcesApi.list(workspaceId),
      ]);
      setVersions(Array.isArray(v) ? v : []);
      setSources(Array.isArray(s) ? s : []);
    } catch (err) {
      console.error(err);
      setLoadError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to load ontology versions'
      );
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const loadBinding = useCallback(async () => {
    if (!workspaceId || !isUuidString(workspaceId)) {
      setBinding(null);
      return;
    }
    try {
      const b = await ontologyApi.getWorkspaceOntologyGraph(workspaceId);
      setBinding(b || null);
    } catch (err) {
      console.error('Failed to load ontology graph binding:', err);
      setBinding(null);
    }
  }, [workspaceId]);

  useEffect(() => { loadBinding(); }, [loadBinding]);

  const loadLatestRun = useCallback(async () => {
    if (!workspaceId || !isUuidString(workspaceId)) {
      setLatestRun(null);
      return;
    }
    try {
      const data = await ontologyApi.getLatestMaterialization(workspaceId);
      setLatestRun((data && data.run) || null);
    } catch (err) {
      console.error('Failed to load latest materialization run:', err);
      setLatestRun(null);
    }
  }, [workspaceId]);

  useEffect(() => { loadLatestRun(); }, [loadLatestRun]);

  const openReportModal = async (runId) => {
    if (!runId) return;
    setReportError(null);
    setReportLoading(true);
    setReportModal({ runId, report: null });
    try {
      const data = await ontologyApi.getValidationReport(runId);
      setReportModal({ runId, report: data });
    } catch (err) {
      setReportError(err?.response?.data?.detail || err.message || 'Failed to load validation report');
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportModal = () => {
    setReportModal(null);
    setReportError(null);
    setReportLoading(false);
  };

  const loadVersion = async (id) => {
    setSelected(id);
    try {
      const d = await ontologyApi.getVersion(id);
      setDetail(d);
    } catch (err) { alert('Failed to load version'); }
  };

  const handleSynthesize = async () => {
    const sourceIds = sources.map(s => s.source_id);
    if (!sourceIds.length) { alert('No sources registered'); return; }
    setSynthLoading(true);
    try {
      const result = await ontologyApi.synthesize({
        workspace_id: workspaceId,
        source_ids: sourceIds,
        notes: 'Synthesized from UI',
      });
      alert(`Ontology v${result.version_number}: ${result.entity_types} entities, ${result.relationships} relationships`);
      load();
    } catch (err) {
      alert('Synthesis error: ' + (err.response?.data?.detail || err.message));
    }
    setSynthLoading(false);
  };

  const openPublishModal = async (row) => {
    setPublishError(null);
    setPublishModal({ versionId: row.version_id, versionNumber: row.version_number });
    const ws = (workspaceId || '').trim();
    setPublishWorkspace(isUuidString(ws) ? ws : '');
    setPublishGraphName(`EDNL Ontology v${row.version_number}`);
    setPublishWorkspaces([]);
    setPublishWorkspacesLoading(true);
    try {
      const resp = await apiService.getWorkspacesForDropdown();
      const list = (resp && resp.workspaces) || [];
      setPublishWorkspaces(list);
      if (ws && !list.some((w) => w.workspaceId === ws)) {
        setPublishWorkspace(list[0]?.workspaceId || '');
      }
    } catch (err) {
      console.error('Failed to load workspaces for publish dialog:', err);
    } finally {
      setPublishWorkspacesLoading(false);
    }
  };

  const closePublishModal = () => {
    setPublishModal(null);
    setPublishError(null);
    setPublishLoading(false);
  };

  const submitPublish = async (e) => {
    e.preventDefault();
    if (!publishModal) return;
    setPublishError(null);
    const ws = publishWorkspace.trim();
    if (!isUuidString(ws)) {
      setPublishError('Please select a workspace.');
      return;
    }
    const gname = publishGraphName.trim();
    if (!gname) {
      setPublishError('Graph name is required.');
      return;
    }
    const currentUser = getCurrentUser();
    const createUser =
      (currentUser &&
        (currentUser.email ||
          `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim())) ||
      'ednl_ui';
    const currentUserId =
      currentUser && typeof currentUser.userId === 'string' && isUuidString(currentUser.userId)
        ? currentUser.userId
        : null;
    if (!currentUserId) {
      setPublishError(
        'Could not determine your user ID from the session. Please sign out and sign in again, then retry.',
      );
      return;
    }
    setPublishLoading(true);
    try {
      const body = {
        workspace_id: ws,
        graph_name: gname,
        create_user: createUser.slice(0, 120),
        user_id: currentUserId,
        allow_overwrite: false,
      };
      const r = await ontologyApi.publishToSNN(publishModal.versionId, body);
      closePublishModal();
      alert(
        `Graph saved: ${r.graph_name || gname}\n` +
          `graph_id=${r.graph_id}\n` +
          `${r.nodes} nodes, ${r.synapses} synapses (persisted=${r.persisted})`,
      );
    } catch (err) {
      setPublishError(err.response?.data?.detail || err.message || 'Publish failed');
    } finally {
      setPublishLoading(false);
    }
  };

  const openSourceDocModal = async (row) => {
    setSourceDocError(null);
    setSourceDocCopied(false);
    setSourceDocMarkdown('');
    setSourceDocFilename('');
    setSourceDocModal({ versionId: row.version_id, versionNumber: row.version_number });
    setSourceDocLoading(true);
    try {
      const r = await ontologyApi.getGraphSourceDocument(row.version_id);
      setSourceDocMarkdown(r.markdown || '');
      setSourceDocFilename(r.suggested_filename || `ednl-ontology-v${row.version_number}.md`);
    } catch (err) {
      setSourceDocError(err.response?.data?.detail || err.message || 'Failed to render document');
    } finally {
      setSourceDocLoading(false);
    }
  };

  const closeSourceDocModal = () => {
    setSourceDocModal(null);
    setSourceDocError(null);
    setSourceDocMarkdown('');
    setSourceDocFilename('');
    setSourceDocCopied(false);
    setSourceDocLoading(false);
  };

  const handleCopySourceDoc = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(sourceDocMarkdown);
      } else {
        const ta = document.createElement('textarea');
        ta.value = sourceDocMarkdown;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setSourceDocCopied(true);
      setTimeout(() => setSourceDocCopied(false), 2000);
    } catch (err) {
      setSourceDocError('Copy failed: ' + (err.message || err));
    }
  };

  const handleDownloadSourceDoc = () => {
    try {
      const blob = new Blob([sourceDocMarkdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sourceDocFilename || 'ednl-ontology.md';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSourceDocError('Download failed: ' + (err.message || err));
    }
  };

  const handleSaveBinding = async (e) => {
    e.preventDefault();
    setBindingError(null);
    if (!isUuidString(workspaceId)) {
      setBindingError('Workspace UUID is not set. Select a workspace first.');
      return;
    }
    if (!isUuidString(bindingGraphId)) {
      setBindingError('Ontology graph UUID is required.');
      return;
    }
    setBindingLoading(true);
    try {
      const body = {
        ontology_graph_id: bindingGraphId.trim(),
        ontology_graph_name: bindingGraphName.trim() || null,
        mark_materialized: false,
      };
      const r = await ontologyApi.setWorkspaceOntologyGraph(workspaceId, body);
      setBinding(r || null);
      setBindingGraphId('');
      setBindingGraphName('');
    } catch (err) {
      setBindingError(err.response?.data?.detail || err.message || 'Failed to save binding');
    } finally {
      setBindingLoading(false);
    }
  };

  const handleClearBinding = async () => {
    if (!isUuidString(workspaceId)) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Clear the ontology graph binding for this workspace?')) return;
    setBindingLoading(true);
    setBindingError(null);
    try {
      await ontologyApi.clearWorkspaceOntologyGraph(workspaceId);
      setBinding(null);
    } catch (err) {
      setBindingError(err.response?.data?.detail || err.message || 'Failed to clear binding');
    } finally {
      setBindingLoading(false);
    }
  };

  const handleMaterialize = async (versionId) => {
    if (!isUuidString(workspaceId)) {
      alert('Select a workspace first.');
      return;
    }
    setMaterializeLoading(versionId);
    try {
      const r = await ontologyApi.materialize(versionId, { workspace_id: workspaceId });
      alert(
        `Materialization ${r.status || 'complete'}.\n` +
        `Run ID: ${(r.run_id || '').slice(0, 8)}…\n` +
        `Nodes: ${r.nodes ?? '?'}, Synapses: ${r.synapses ?? '?'}`
      );
      loadBinding();
      loadLatestRun();
    } catch (err) {
      alert('Materialize error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMaterializeLoading(null);
    }
  };

  const handleDiff = async () => {
    if (!diffOld || !diffNew) { alert('Select two versions'); return; }
    try {
      const r = await ontologyApi.diff(diffOld, diffNew);
      setDiffResult(r);
    } catch (err) { alert('Diff error'); }
  };

  return (
    <div>
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Workspace Ontology Graph</h2>
        </div>
        <p className="ednl-card__subtitle" style={{ margin: '0 0 12px 0' }}>
          The ontology graph is the durable runtime anchor used by every EDNL module
          (templates, patterns, frames, bridge, audit, live stream). Bind an existing
          graph of type <code>Ontology</code>, or create one via <strong>Create Graph (Export)</strong> below.
        </p>
        {!isUuidString(workspaceId) ? (
          <div className="ednl-inline-alert ednl-inline-alert--warning" role="alert">
            Workspace UUID is not set. Select a workspace in User Home so EDNL can bind an ontology graph.
          </div>
        ) : binding && binding.ontology_graph_id ? (
          <div>
            <div className="ednl-table-wrap" style={{ marginBottom: 12 }}>
              <table className="ednl-table">
                <tbody>
                  <tr><th>Graph ID</th><td className="mono">{binding.ontology_graph_id}</td></tr>
                  <tr><th>Graph name</th><td>{binding.ontology_graph_name || '—'}</td></tr>
                  <tr><th>Source version</th><td>{binding.ontology_version_number ? `v${binding.ontology_version_number}` : '—'} <span className="mono" style={{ color: '#6b7280', marginLeft: 8 }}>{binding.ontology_version_id || ''}</span></td></tr>
                  <tr><th>Last materialized</th><td>{binding.last_materialized_at || '—'}</td></tr>
                  <tr>
                    <th>Precision</th>
                    <td>
                      {latestRun ? (
                        <PrecisionBadgeInline
                          run={latestRun}
                          onOpenReport={() => openReportModal(latestRun.run_id)}
                        />
                      ) : (
                        <span style={{ color: '#9ca3af' }}>No materialization run recorded yet.</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="ednl-btn-group">
              <button className="ednl-btn" onClick={loadBinding} disabled={bindingLoading}>Refresh</button>
              <button
                className="ednl-btn"
                onClick={() => { loadBinding(); loadLatestRun(); }}
                disabled={bindingLoading}
              >
                Refresh precision
              </button>
              <button className="ednl-btn" onClick={handleClearBinding} disabled={bindingLoading}>Clear binding</button>
            </div>
          </div>
        ) : (
          <form className="ednl-form" onSubmit={handleSaveBinding}>
            <div className="ednl-inline-alert" role="note" style={{ marginBottom: 12 }}>
              No ontology graph is bound to this workspace yet.
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group">
                <label className="ednl-label">Ontology graph UUID</label>
                <input
                  className="ednl-input"
                  value={bindingGraphId}
                  onChange={(e) => setBindingGraphId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">Display name (optional)</label>
                <input
                  className="ednl-input"
                  value={bindingGraphName}
                  onChange={(e) => setBindingGraphName(e.target.value)}
                />
              </div>
            </div>
            {bindingError && (
              <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
                {bindingError}
              </div>
            )}
            <div className="ednl-btn-group">
              <button type="submit" className="ednl-btn ednl-btn--primary" disabled={bindingLoading}>
                {bindingLoading ? 'Saving…' : 'Bind Graph'}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Ontology Versions</h2>
          <button className="ednl-btn ednl-btn--primary" onClick={handleSynthesize} disabled={synthLoading}>
            {synthLoading ? 'Synthesizing...' : 'Synthesize New Version'}
          </button>
        </div>

        {loadError && (
          <div
            className="ednl-inline-alert ednl-inline-alert--error"
            role="alert"
            style={{ marginBottom: 12 }}
          >
            {loadError}
          </div>
        )}

        {loading ? <div className="ednl-loading">Loading...</div> : versions.length === 0 ? (
          <div className="ednl-empty">
            <div className="ednl-empty__icon">🧬</div>
            <div className="ednl-empty__text">No ontology versions yet. Synthesize one from your sources.</div>
          </div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Entities</th>
                  <th>Relationships</th>
                  <th>Created</th>
                  <th>Published graphs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.version_id} style={{ background: selected === v.version_id ? '#f0f0ff' : undefined }}>
                    <td>v{v.version_number}</td>
                    <td>{v.entity_types}</td>
                    <td>{v.relationships}</td>
                    <td>{v.created_at?.slice(0, 19)}</td>
                    <td>
                      {(v.published_graphs || []).length === 0 ? (
                        <span style={{ color: '#6b7280' }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {v.published_graphs.map(g => (
                            <span
                              key={g.graph_id}
                              title={g.graph_id}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                              <span>{g.graph_name}</span>
                              <button
                                type="button"
                                className="ednl-btn ednl-btn--sm"
                                onClick={() => navigator.clipboard.writeText(g.graph_id)}
                                title={`Copy ${g.graph_id}`}
                              >
                                copy id
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => loadVersion(v.version_id)}>View</button>
                        <button
                          type="button"
                          className="ednl-btn ednl-btn--sm ednl-btn--primary"
                          onClick={() => openSourceDocModal(v)}
                          title="Export a Markdown document you can upload to a new graph"
                        >
                          Create Graph (Export)
                        </button>
                        <button
                          type="button"
                          className="ednl-btn ednl-btn--sm ednl-btn--primary"
                          onClick={() => handleMaterialize(v.version_id)}
                          disabled={materializeLoading === v.version_id}
                          title="Materialize ontology into the workspace graph with round-trip validation"
                        >
                          {materializeLoading === v.version_id ? 'Materializing…' : 'Materialize'}
                        </button>
                        <button
                          type="button"
                          className="ednl-btn ednl-btn--sm"
                          onClick={() => openPublishModal(v)}
                          title="Advanced: persist ontology directly as a Graph via publish pipeline"
                        >
                          Publish (advanced)
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="ednl-card">
          <h2 className="ednl-card__title">v{detail.version_number} Detail</h2>
          <h3 style={{ fontWeight: 600, margin: '12px 0 8px' }}>Entity Types ({detail.entity_types?.length || 0})</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {(detail.entity_types || []).map(e => (
              <div key={e.entity_type_id} className="ednl-panel" style={{ minWidth: 280, flex: '1 1 280px' }}>
                <div className="ednl-panel__header">{e.name} ({e.attributes?.length || 0} attrs)</div>
                <div className="ednl-panel__body">
                  <p className="ednl-hint" style={{ marginBottom: 6 }}>Tables: {e.source_tables?.join(', ')}</p>
                  <table className="ednl-table">
                    <thead><tr><th>Attribute</th><th>Business Name</th><th>Type</th></tr></thead>
                    <tbody>
                      {(e.attributes || []).slice(0, 12).map(a => (
                        <tr key={a.name}><td className="mono">{a.name}</td><td>{a.business_name}</td><td className="mono">{a.data_type}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <h3 style={{ fontWeight: 600, margin: '16px 0 8px' }}>Relationships ({detail.relationships?.length || 0})</h3>
          {(detail.relationships || []).length > 0 && (
            <div className="ednl-table-wrap">
              <table className="ednl-table">
                <thead><tr><th>Name</th><th>Source</th><th>Target</th><th>Cardinality</th><th>Method</th><th>Confidence</th></tr></thead>
                <tbody>
                  {detail.relationships.map(r => (
                    <tr key={r.relationship_id}><td>{r.name}</td><td>{r.source_entity}</td><td>{r.target_entity}</td><td>{r.cardinality}</td><td>{r.discovery_method}</td><td>{r.confidence}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(detail.event_patterns || []).length > 0 && (
            <>
              <h3 style={{ fontWeight: 600, margin: '16px 0 8px' }}>Event Patterns ({detail.event_patterns.length})</h3>
              <JsonViewer data={detail.event_patterns} label="Patterns" defaultOpen />
            </>
          )}
        </div>
      )}

      {publishModal && (
        <div className="ednl-dialog-overlay" role="presentation" onClick={publishLoading ? undefined : closePublishModal}>
          <div className="ednl-dialog" style={{ maxWidth: 520 }} role="dialog" aria-labelledby="ednl-publish-title" onClick={(ev) => ev.stopPropagation()}>
            <h3 id="ednl-publish-title" className="ednl-dialog__title">
              Publish ontology v{publishModal.versionNumber} to database
            </h3>
            <p className="ednl-dialog__message">
              Creates a real <code>CerebralMachines.Graph</code> row, NodeKind/SynapseKind entries as needed, and nodes/synapses via the same insert path as the document graph builder.
            </p>
            {publishError && (
              <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
                {publishError}
              </div>
            )}
            <form onSubmit={submitPublish}>
              <label className="ednl-label" htmlFor="ednl-pub-ws">Workspace</label>
              <select
                id="ednl-pub-ws"
                className="ednl-input"
                value={publishWorkspace}
                onChange={(e) => setPublishWorkspace(e.target.value)}
                disabled={publishLoading || publishWorkspacesLoading}
                required
              >
                {publishWorkspacesLoading && (
                  <option value="">Loading workspaces...</option>
                )}
                {!publishWorkspacesLoading && publishWorkspaces.length === 0 && (
                  <option value="">No workspaces available</option>
                )}
                {!publishWorkspacesLoading && publishWorkspaces.length > 0 && (
                  <>
                    <option value="">-- Select a workspace --</option>
                    {publishWorkspaces.map((w) => (
                      <option key={w.workspaceId} value={w.workspaceId}>
                        {w.workspaceName}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <label className="ednl-label" htmlFor="ednl-pub-name" style={{ marginTop: 12, display: 'block' }}>
                Graph display name
              </label>
              <input
                id="ednl-pub-name"
                className="ednl-input"
                value={publishGraphName}
                onChange={(e) => setPublishGraphName(e.target.value)}
                disabled={publishLoading}
                required
              />
              <label className="ednl-label" style={{ marginTop: 12, display: 'block' }}>
                Create user (auto)
              </label>
              <div className="ednl-input" style={{ background: '#f9fafb', color: '#6b7280' }}>
                {(() => {
                  const u = getCurrentUser();
                  return (
                    (u && (u.email || `${u.firstName || ''} ${u.lastName || ''}`.trim())) ||
                    'ednl_ui'
                  );
                })()}
              </div>
              <div className="ednl-dialog__actions" style={{ marginTop: 20 }}>
                <button type="button" className="ednl-btn" onClick={closePublishModal} disabled={publishLoading}>
                  Cancel
                </button>
                <button type="submit" className="ednl-btn ednl-btn--primary" disabled={publishLoading}>
                  {publishLoading ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sourceDocModal && (
        <div className="ednl-dialog-overlay" role="presentation" onClick={sourceDocLoading ? undefined : closeSourceDocModal}>
          <div className="ednl-dialog" style={{ maxWidth: 820 }} role="dialog" aria-labelledby="ednl-source-doc-title" onClick={(ev) => ev.stopPropagation()}>
            <h3 id="ednl-source-doc-title" className="ednl-dialog__title">
              Ontology v{sourceDocModal.versionNumber} — Graph Source Document
            </h3>
            <p className="ednl-dialog__message">
              Copy or download this document, then create a new graph of type <code>Ontology</code> in{' '}
              <strong>User Home → Graphs</strong> and upload the file as the source document.
              The normal chunking and graph builder will materialize the ontology.
            </p>
            {sourceDocError && (
              <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
                {sourceDocError}
              </div>
            )}
            {sourceDocLoading ? (
              <div className="ednl-loading">Rendering…</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <button type="button" className="ednl-btn ednl-btn--primary" onClick={handleCopySourceDoc}>
                    {sourceDocCopied ? 'Copied ✓' : 'Copy to clipboard'}
                  </button>
                  <button type="button" className="ednl-btn" onClick={handleDownloadSourceDoc}>
                    Download .md
                  </button>
                  <span className="mono" style={{ color: '#6b7280', fontSize: 12 }}>
                    {sourceDocFilename}
                  </span>
                </div>
                <textarea
                  className="ednl-input"
                  style={{ width: '100%', minHeight: 340, fontFamily: 'monospace' }}
                  readOnly
                  value={sourceDocMarkdown}
                />
              </>
            )}
            <div className="ednl-dialog__actions" style={{ marginTop: 16 }}>
              <button type="button" className="ednl-btn" onClick={closeSourceDocModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {versions.length >= 2 && (
        <div className="ednl-card">
          <h2 className="ednl-card__title">Version Diff</h2>
          <div className="ednl-form-row" style={{ marginBottom: 12 }}>
            <div className="ednl-form-group">
              <label className="ednl-label">Old Version</label>
              <select className="ednl-select" value={diffOld} onChange={e => setDiffOld(e.target.value)}>
                <option value="">Select...</option>
                {versions.map(v => <option key={v.version_id} value={v.version_id}>v{v.version_number}</option>)}
              </select>
            </div>
            <div className="ednl-form-group">
              <label className="ednl-label">New Version</label>
              <select className="ednl-select" value={diffNew} onChange={e => setDiffNew(e.target.value)}>
                <option value="">Select...</option>
                {versions.map(v => <option key={v.version_id} value={v.version_id}>v{v.version_number}</option>)}
              </select>
            </div>
            <div className="ednl-form-group" style={{ justifyContent: 'flex-end' }}>
              <button className="ednl-btn ednl-btn--primary" onClick={handleDiff}>Compare</button>
            </div>
          </div>
          {diffResult && <JsonViewer data={diffResult} label="Diff Results" defaultOpen />}
        </div>
      )}

      {reportModal && (
        <div
          className="ednl-dialog-overlay"
          role="presentation"
          onClick={reportLoading ? undefined : closeReportModal}
        >
          <div
            className="ednl-dialog"
            style={{ maxWidth: 860 }}
            role="dialog"
            aria-labelledby="ednl-validation-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 id="ednl-validation-title" className="ednl-dialog__title">
              Ontology materialization precision report
            </h3>
            <p className="ednl-dialog__message">
              Round-trip check between the <code>OntologyVersion</code> and the materialized graph.
              A precision failure means something was dropped or drifted; the workspace binding was
              only flipped to this graph if the check passed (or if force was used).
            </p>
            {reportError && (
              <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
                {reportError}
              </div>
            )}
            {reportLoading ? (
              <div className="ednl-loading">Loading report…</div>
            ) : reportModal.report ? (
              <ValidationReportView data={reportModal.report} />
            ) : null}
            <div className="ednl-dialog__actions" style={{ marginTop: 16 }}>
              <button type="button" className="ednl-btn" onClick={closeReportModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PrecisionBadgeInline({ run, onOpenReport }) {
  if (!run) return <span style={{ color: '#9ca3af' }}>n/a</span>;
  const status = run.status || 'UNKNOWN';
  const passed = status === 'COMPLETED';
  const failed = status === 'VALIDATED_FAIL' || status === 'FAILED';
  const label = passed ? 'PASS' : failed ? 'FAIL' : status;
  const bg = passed ? '#dcfce7' : failed ? '#fee2e2' : '#f3f4f6';
  const color = passed ? '#14532d' : failed ? '#991b1b' : '#374151';
  const border = passed ? '#16a34a' : failed ? '#dc2626' : '#9ca3af';
  return (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span
        style={{
          background: bg,
          color,
          border: `1px solid ${border}`,
          padding: '2px 10px',
          borderRadius: 9999,
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: 0.5,
        }}
      >
        {label}
      </span>
      <span style={{ color: '#6b7280', fontSize: 12 }}>
        run <span className="mono">{(run.run_id || '').slice(0, 8)}</span>
        {run.node_count != null ? ` · ${run.node_count} nodes` : ''}
        {run.synapse_count != null ? ` · ${run.synapse_count} synapses` : ''}
      </span>
      <button
        type="button"
        className="ednl-btn"
        style={{ padding: '2px 8px', fontSize: 12 }}
        onClick={onOpenReport}
      >
        View report
      </button>
    </span>
  );
}


function ValidationReportView({ data }) {
  if (!data) return null;
  const report = data.validation_report || data;
  const summary = report.summary || {};
  const passed = !!report.pass;
  const rows = [
    ['Entities', summary.entities_actual, summary.entities_expected],
    ['Attributes', summary.attrs_actual, summary.attrs_expected],
    ['Relationships', summary.rels_actual, summary.rels_expected],
  ];
  const section = (title, arr, render) => {
    if (!arr || arr.length === 0) return null;
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{title} ({arr.length})</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {arr.slice(0, 20).map((item, idx) => (
            <li key={idx} style={{ fontFamily: 'monospace', fontSize: 12 }}>{render(item)}</li>
          ))}
          {arr.length > 20 && (
            <li style={{ color: '#6b7280' }}>… {arr.length - 20} more not shown</li>
          )}
        </ul>
      </div>
    );
  };
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        Status:{' '}
        <strong style={{ color: passed ? '#14532d' : '#991b1b' }}>
          {passed ? 'PASS' : 'FAIL'}
        </strong>
        {data.status ? <span style={{ marginLeft: 8, color: '#6b7280' }}>run status: {data.status}</span> : null}
      </div>
      <div className="ednl-table-wrap">
        <table className="ednl-table">
          <thead>
            <tr><th>Metric</th><th>Actual</th><th>Expected</th></tr>
          </thead>
          <tbody>
            {rows.map(([lbl, act, exp]) => (
              <tr key={lbl}>
                <td>{lbl}</td>
                <td>{act ?? '—'}</td>
                <td>{exp ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {section('Missing entities', report.missing_entities, (x) => x)}
      {section('Extra entities', report.extra_entities, (x) => x)}
      {section(
        'Attribute count mismatches',
        report.attribute_count_mismatches,
        (m) => `${m.entity}: expected ${m.expected}, actual ${m.actual}`,
      )}
      {section(
        'Relationship count mismatches',
        report.relationship_count_mismatches,
        (m) => `${m.source_entity}: expected ${m.expected}, actual ${m.actual}`,
      )}
      {section(
        'Missing relationships',
        report.missing_relationships,
        (m) => `${m.source} -[${m.name}]-> ${m.target}`,
      )}
      {section(
        'Entity fingerprint mismatches',
        report.fingerprint_mismatches,
        (m) => `${m.entity}`,
      )}
      {(!report.missing_entities?.length
        && !report.attribute_count_mismatches?.length
        && !report.relationship_count_mismatches?.length
        && !report.missing_relationships?.length
        && !report.fingerprint_mismatches?.length) && (
        <div style={{ marginTop: 12, color: '#14532d' }}>
          No drift detected — graph exactly matches the ontology version.
        </div>
      )}
    </div>
  );
}
